import React, { PureComponent } from "react";
import Link from "next/link";
import { Query } from "@apollo/client/react/components";
import Grid from "./Grid";
import { curveStep } from "d3-shape";
import moment from "moment-timezone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowCircleRight, faBullseye, faDollarSign, faUserFriends } from "@fortawesome/free-solid-svg-icons";
import ReactResizeDetector from "react-resize-detector";

import styles from "./styles.scss";
import Filters from "./Filters";
import LineChart from "./LineChart";
import YAxis from "./YAxis";
import XAxis from "./XAxis";
import ButtonGroup, { ButtonGroupItem } from "../ButtonGroup";
import Dropdown from "../Dropdown";
import { sortByDate } from "../../services/DateService";
import { BOOKS_QUERY } from "../../gql/queries";
import { WEB_SPORTS_MAP } from "../../constants";
import { PICK_TYPES } from "src/common-services";
import { PERIODS } from "src/services/EventService";

export interface ChartProps {
  game: any;
  showData: boolean;
  sportKey: string;
  style?: any;
}

export interface ChartState {
  currentBookIndex: number;
  currentTeamIndex: number;
  filterType: string;
}

class Chart extends PureComponent<ChartProps, ChartState> {
  constructor(props: ChartProps) {
    super(props);
    const currentSportObj = WEB_SPORTS_MAP[props.sportKey];
    this.state = {
      currentBookIndex: 0,
      currentTeamIndex: currentSportObj.hasDraws ? 2 : 1,
      filterType: currentSportObj.favoriteLine,
    };
  }

  handleTeamChange = (_: ButtonGroupItem, currentTeamIndex: number) => {
    this.setState({ currentTeamIndex });
  };

  handleBookChange = (_: ButtonGroupItem, currentBookIndex: number) => {
    this.setState({ currentBookIndex });
  };

  handleSelectFilter = (filter: any) => {
    const { key: filterType } = filter;
    this.setState({ filterType });
  };

  render() {
    const { game, showData, sportKey } = this.props;
    if (!game.id) {
      return null;
    }
    const currentSportObj = WEB_SPORTS_MAP[sportKey];
    const { currentBookIndex, currentTeamIndex, filterType } = this.state;

    const teams = [
      {
        key: "away",
        name: game.awayTeam.preferredAbbreviation,
      },
      {
        key: "home",
        name: game.homeTeam.preferredAbbreviation,
      },
    ];

    if (currentSportObj.hasDraws) {
      teams.splice(1, 0, { key: "draw", name: "Draw" });
    }

    const selectedTeam = teams[currentTeamIndex].key;

    const getLines = (lines: any) => {
      const currentLine = game[`current${PICK_TYPES[filterType].linePeriodKey}Line`];
      if (!currentLine) {
        return [];
      }
      let dataKey: any,
        periodKey = "FG";
      if (filterType !== "spread" && filterType !== "moneyline" && filterType !== "total") {
        periodKey = filterType.substring(0, 2);
      }
      if (filterType.includes("moneyline")) {
        dataKey = `${selectedTeam}Money`;
      }
      if (filterType.includes("spread")) {
        dataKey = `${selectedTeam}Spread`;
      }
      if (filterType.includes("total")) {
        dataKey = "total";
      }

      return dataKey === undefined
        ? []
        : lines
            .reduce((array: any, line: any) => {
              if (line[dataKey] != null && line.period === periodKey) {
                array.push({
                  type: line.lineType,
                  line: line[dataKey],
                  startDate: moment(line.time),
                });
              }
              return array;
            }, [])
            .sort(sortByDate);
    };

    const yAccessor = ({ item }: { item: any }) => item.line;
    const yLabel = (value: any) => value;
    const xAccessor = ({ item }: { item: any }) => item.startDate;
    const xLabel = (value: any, index: number, lastIndex: number) => {
      if (index === 0) {
        return "Open";
      }
      if (index === lastIndex) {
        return "Current";
      }
      return moment(value).format("ha M/D");
    };

    return (
      <Query
        ssr={false}
        query={BOOKS_QUERY}
        variables={{
          eventId: game.id,
          period: Object.keys(PERIODS),
        }}
      >
        {({ data: booksData, loading: booksLoading, errors }: any) => {
          // Gather books for league
          let books = booksData?.books ?? [];
          // Fallback (ie while loading)
          books = books.length
            ? books
            : [
                {
                  name: "Consensus",
                  rank: 0,
                },
              ];

          const linesdata = books[currentBookIndex]?.lines || [];
          const data = getLines(linesdata);

          let yMax: any;
          let yMin: any;
          data.forEach((line: any) => {
            if (undefined === yMin || line.line < yMin) {
              yMin = line.line;
            }
            if (undefined === yMax || line.line > yMax) {
              yMax = line.line;
            }
          });
          const xMin = data[0] ? moment(data[0].startDate).valueOf() : 0;
          const xMax = data[data.length - 1] ? moment(data[data.length - 1].startDate).valueOf() : 0;
          let yStep = parseInt((yMax - yMin) / 0.5 + "", 10);
          yStep = yStep > 5 ? 5 : yStep < 3 ? 3 : yStep;
          const contentInset = { top: 15, bottom: 5 };

          return (
            <div className="rotoql-chart__container">
              <div className="rotoql-chart__line-movement">
                <div className="rotoql-chart__nav">
                  <div className="rotoql-chart__title">Line Movement</div>

                  <div className="rotoql-chart__button-group">
                    <ButtonGroup onSelectItem={this.handleTeamChange} selectedIndex={currentTeamIndex} items={teams} />
                  </div>

                  <div className="rotoql-chart__dropdown">
                    <Dropdown onSelectItem={this.handleBookChange} selectedIndex={currentBookIndex} items={books} />
                  </div>
                </div>
                <div className="rotoql-chart__filters">
                  <Filters filterType={filterType} onSelectFilter={this.handleSelectFilter} sportKey={sportKey} />
                </div>
                <div className="rotoql-chart__inner">
                  {booksLoading && !booksData?.lines ? <p className="rotoql-chart__text">Loading graph...</p> : null}
                  {!data.length || errors?.length ? (
                    <p className="rotoql-chart__text">
                      No data for graph. Lines will begin to move closer to the start of the game.
                    </p>
                  ) : null}
                  {data.length ? (
                    <ReactResizeDetector handleWidth>
                      {({ width, height }: { height: number; width: number }) => {
                        width = width ?? 0;
                        height = height ?? 0;
                        return (
                          <div className="rotoql-chart__chart-container">
                            <YAxis
                              yAccessor={yAccessor}
                              data={data}
                              max={yMax}
                              min={yMin}
                              numberOfTicks={yStep}
                              svg={{
                                fill: "#515151",
                                fontWeight: "500",
                              }}
                              formatLabel={yLabel}
                              contentInset={contentInset}
                              height={height - 45}
                            />
                            <div style={{ flex: 1 }}>
                              <LineChart
                                height={height - 45}
                                width={width - 90}
                                yAccessor={yAccessor}
                                yMax={yMax}
                                yMin={yMin}
                                xMax={xMax}
                                xMin={xMin}
                                xAccessor={xAccessor}
                                numberOfTicks={yStep}
                                curve={curveStep}
                                data={data}
                                svg={{
                                  stroke: showData ? "#004198" : "transparent",
                                  strokeWidth: 2.5,
                                }}
                                contentInset={{
                                  top: 15,
                                  bottom: 5,
                                  left: 15,
                                  right: 15,
                                }}
                              >
                                <Grid />
                              </LineChart>
                              <XAxis
                                width={width - 90}
                                xAccessor={xAccessor}
                                formatLabel={xLabel}
                                data={data}
                                max={xMax}
                                min={xMin}
                                numberOfTicks={5}
                                contentInset={{
                                  bottom: 0,
                                  left: 15,
                                  right: 15,
                                }}
                                svg={{ fontSize: 10, fill: "black" }}
                              />
                            </div>
                          </div>
                        );
                      }}
                    </ReactResizeDetector>
                  ) : null}
                </div>
                <div className="rotoql-chart__dropdown-mobile">
                  <Dropdown onSelectItem={this.handleBookChange} selectedIndex={currentBookIndex} items={books} />
                </div>
              </div>
              <div className="rotoql-chart__more-analysis">
                <div className="rotoql-chart__more-analysis-inner">
                  <div className="rotoql-chart__title">More Analysis</div>
                  <div className="rotoql-chart__more-analysis-item">
                    <FontAwesomeIcon icon={faBullseye} />
                    <div className="rotoql-chart__more-analysis-item-inner">
                      <div className="rotoql-chart__subtitle">Pro Bettor Report</div>
                      <div className="rotoql-chart__header">View who the professional bettors like today.</div>
                    </div>
                    <Link passHref href={`/${currentSportObj.url}/sharp-picks`} legacyBehavior>
                      <a>
                        <FontAwesomeIcon icon={faArrowCircleRight} color="#004198" />
                      </a>
                    </Link>
                  </div>
                  <div className="rotoql-chart__more-analysis-item">
                    <FontAwesomeIcon icon={faUserFriends} />
                    <div className="rotoql-chart__more-analysis-item-inner">
                      <div className="rotoql-chart__subtitle">Public Bets</div>
                      <div className="rotoql-chart__header">See who the public is betting today.</div>
                    </div>
                    <Link passHref href={`/${currentSportObj.url}/public-betting`} legacyBehavior>
                      <a>
                        <FontAwesomeIcon icon={faArrowCircleRight} color="#004198" />
                      </a>
                    </Link>
                  </div>
                  <div className="rotoql-chart__more-analysis-item">
                    <FontAwesomeIcon icon={faDollarSign} />
                    <div className="rotoql-chart__more-analysis-item-inner">
                      <div className="rotoql-chart__subtitle">The Best Offers</div>
                      <div className="rotoql-chart__header">Find the best offers in your state for today's games.</div>
                    </div>
                    <Link href="/sportsbook-offers" legacyBehavior>
                      <a>
                        <FontAwesomeIcon icon={faArrowCircleRight} color="#004198" />
                      </a>
                    </Link>
                  </div>
                </div>
              </div>
              <style jsx global>
                {styles}
              </style>
            </div>
          );
        }}
      </Query>
    );
  }
}

export default Chart;
